import { Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import RichTextInput from "ra-input-rich-text";
import React from "react";
import {
  ArrayField,
  ArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  EditButton,
  ExportButton,
  Filter,
  FormDataConsumer,
  FormTab,
  FunctionField,
  List,
  NullableBooleanInput,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  required,
  RichTextField,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  Show,
  ShowButton,
  SimpleFormIterator,
  SingleFieldList,
  Tab,
  TabbedForm,
  TabbedFormTabs,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
} from "react-admin";
import EditReadOnlyButton from "../components/Buttons/EditReadOnlyButton";
// eslint-disable-next-line import/no-unresolved
import EditTravelPolicyButton from "../components/Buttons/EditTravelPolicyButton";
import ChipField from "../components/Fields/ChipField";
import NoBasePath from "../components/Fields/NoBasePath";
import PlaceInput from "../components/Input/PlaceInput";
import SabreConfigInput from "../components/Input/SabreConfigInput";
import SidebarFormLayout from "../components/SidebarFormLayout";
import {
  airProviders,
  AmadeusConfigsInputs,
  carRentalProviders,
  categoryMessages,
  hotelProviders,
  railProviders,
  SabreConfigsFields,
} from "./agencies";

const styles = {
  title: {
    marginTop: "30px",
    marginBottom: "15px",
    fontSize: "20px",
    fontWeight: "bold",
  },
  subtitle: {
    marginTop: "24px",
    fontWeight: "bold",
  },
};

export const agencies = [
  { id: "thetreep", name: "TheTreep" },
  { id: "Carlabella", name: "Carlabella" },
  { id: "Vairon", name: "Vairon" },
];

export const sabreProfileType = [
  { id: "CRP", name: "Société" },
  { id: "TVL", name: "Voyageur" },
];

export const approvalType = [
  { id: "basic", name: "Basique" },
  { id: "hierarchical", name: "Hierarchique" },
  { id: "escalation", name: "Escalade" },
];

const markupFeesProviders = [
  "sncf",
  "sabre",
  "amadeus",
  "duffel",
  "sabre-car",
  "carbookr",
  "CDS",
];

const CompanyFilter = (props) => (
  <Filter {...props}>
    <NumberInput label="ID" source="uid" alwaysOn />
    <TextInput source="name" label="Nom" alwaysOn />
    <TextInput source="domain" label="Domaine" />
    <TextInput source="siret" label="SIRET" />
    <ReferenceInput
      source="agency"
      label="Agence de voyage"
      reference="agencies"
      filterToQuery={(q) => ({ name: q })}
      perPage={50}
      allowEmpty
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <TextInput source="contact" label="Contact" />
    <TextInput source="payment_method" label="Moyen de paiement" />
    <TextInput source="fce" label="FCE" />
    <NullableBooleanInput source="hotel_enabled" label="Hôtel activé" />
    <NullableBooleanInput source="carrental_enabled" label="Location voiture" />
  </Filter>
);

const CompaniesActions = (props) => {
  const {
    bulkActions,
    basePath,
    currentSort,
    displayedFilters,
    exporter,
    filters,
    filterValues,
    onUnselectItems,
    resource,
    selectedIds,
    showFilter,
    total,
  } = props;
  return (
    <TopToolbar>
      {bulkActions &&
        React.cloneElement(bulkActions, {
          basePath,
          filterValues,
          resource,
          selectedIds,
          onUnselectItems,
        })}
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}
      <CreateButton basePath={basePath} />
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filter={filterValues}
        exporter={exporter}
      />
    </TopToolbar>
  );
};

const TravelPolicy = ({ record, ...props }) => (
  <EditTravelPolicyButton record={record} {...props} />
);

const ReadOnlyToggle = ({ record, ...props }) => (
  <EditReadOnlyButton record={record} {...props} entity="companies" />
);

export const CompanyList = ({ permissions, ...props }) => (
  <List
    {...props}
    title="Liste des entreprises"
    bulkActionButtons={false}
    actions={<CompaniesActions />}
    filters={<CompanyFilter />}
    sort={{ field: "id", order: "ASC" }}
  >
    <Datagrid rowclick="show" perPage={200}>
      <TextField source="id" label="Id" />
      <TextField source="name" label="Nom" />
      <TextField source="domain" label="Domaine" sortable={false} />
      <ReferenceField
        source="config.travelAgencyID"
        label="Agence de voyage"
        reference="agencies"
        link="show"
        allowEmpty
      >
        <TextField source="name" />
      </ReferenceField>
      <TravelPolicy />
      <BooleanField
        source="config.readOnly"
        label="Lecture seule"
        sortable={false}
      />
      <ShowButton label="" />
      {permissions != null && permissions.includes("admin") && (
        <EditButton label="" />
      )}
      {permissions != null && permissions.includes("dev") && <ReadOnlyToggle />}
    </Datagrid>
  </List>
);

// ---------------------------------------------------------------------------
// Shared reusable input groups (array-returning functions for React 16 compat)
// ---------------------------------------------------------------------------

export const TravelSearchConfigInputs = () => [
  <Typography key="air-title" variant="h5" style={styles.title}>
    Options de recherche aérienne
  </Typography>,
  <BooleanInput
    key="more-air"
    source="config.isMoreAirChoicesEnabled"
    label="Plus de choix aériens"
    helperText="Devrait toujours être activé — permet de récupérer les tarifs plus efficacement via Sabre et Amadeus. La désactivation est dépréciée."
  />,
  <BooleanInput
    key="extra-offers"
    source="config.isExtraOffersForFlightEnabled"
    label="Offres supplémentaires pour les vols"
    helperText="Combine l'ancien et le nouveau comportement de récupération des offres Amadeus. Recommandé pour une couverture tarifaire complète."
  />,
  <BooleanInput
    key="outside-france"
    source="config.allowFlightsOutsideFrance"
    label="Autoriser les vols hors France"
  />,
  <BooleanInput
    key="hidden-price"
    source="config.HiddenFlightPrice"
    label="Masquer les tarifs avions"
    helperText="Masque les prix des billets d'avion pour les voyageurs."
  />,
  <Typography key="traveler-title" variant="h5" style={styles.title}>
    Formulaire voyageur
  </Typography>,
  <BooleanInput
    key="first-pax"
    source="config.isTravelerFormFirstPaxNotEditable"
    label="Premier passager non modifiable"
    helperText="Rend le premier passager non modifiable sur le formulaire avant le panier."
  />,
];

export const ApprovalSecurityInputs = () => [
  <Typography key="approval-title" variant="h5" style={styles.title}>
    Approbation
  </Typography>,
  <SelectInput
    key="approval-type"
    source="config.approvalType"
    choices={approvalType}
    label="Type d'approbation"
  />,
  <BooleanInput
    key="one-click"
    source="config.oneClickApprovalEnabled"
    label="Approbation en un clic"
  />,
  <BooleanInput
    key="pax-approvers"
    source="config.usePaxApprovers"
    label="Utiliser les approbateurs du passager"
  />,
  <BooleanInput
    key="confirm-approver"
    source="config.sendConfirmationToApprover"
    label="Envoyer la confirmation à l'approbateur"
  />,
  <Typography key="backup-title" variant="h5" style={styles.title}>
    Approbateurs de secours
  </Typography>,
  <BooleanInput
    key="load-backup"
    source="config.loadBackupApproversFromService"
    label="Charger depuis le service"
  />,
  <BooleanInput
    key="send-backup"
    source="config.sendApprovalToBackupImmediately"
    label="Envoyer la demande d'approbation immédiatement à l'approbateur de secours"
  />,
  <Typography key="safety-title" variant="h5" style={styles.title}>
    Sûreté
  </Typography>,
  <BooleanInput
    key="safety-approval"
    source="config.safetyApprovalEnabled"
    label="Approbation de sûreté"
  />,
  <NumberInput
    key="safety-user"
    source="config.safetyApproverUserId"
    label="ID approbateur de sûreté"
  />,
  <NumberInput
    key="risk-level"
    source="config.maxRiskLevelAllowed"
    label="Niveau de risque max"
  />,
  <Typography key="cancel-title" variant="h5" style={styles.title}>
    Annulations
  </Typography>,
  <BooleanInput
    key="rail-cancel"
    source="config.railOfflineCancellation"
    label="Annulation rail offline"
  />,
  <BooleanInput
    key="air-cancel"
    source="config.airOfflineCancellation"
    label="Annulation aérien offline"
  />,
  <BooleanInput
    key="cancel-email"
    source="config.sendCancellationEmailToCustomer"
    label="Email d'annulation au client"
  />,
];

export const AnalyticsConfigInputs = () => [
  <Typography key="analytics-title" variant="h5" style={styles.title}>
    Valeurs analytiques
  </Typography>,
  <ArrayInput
    key="analytic-values"
    source="config.analyticValues"
    label="Valeurs analytiques"
  >
    <SimpleFormIterator>
      <TextInput source="analytic.key" label="Clé SABRE/Amadeus" />
      <TextInput source="analytic.cds_key" label="Clé CDS" />
      <TextInput source="value" label="Valeur" />
    </SimpleFormIterator>
  </ArrayInput>,
  <BooleanInput
    key="enable-travelers"
    source="config.enableTravelersAnalytics"
    label="Analytiques voyageurs éditables"
  />,
  <BooleanInput
    key="disable-prompt"
    source="config.disableAnalyticPrompt"
    label="Désactiver le prompt analytique"
  />,
  <TextInput
    key="user-error"
    source="config.userFacingAnalyticError"
    label="Message d'erreur analytique"
  />,
];

const carRentalSortTypes = [
  { id: "price", name: "Prix" },
  { id: "distance", name: "Distance" },
  { id: "custom", name: "Personnalisé" },
  { id: "renters", name: "Loueurs" },
];

export const CarRentalConfigInputs = () => [
  <Typography key="car-title" variant="h5" style={styles.title}>
    Configuration location de voitures
  </Typography>,
  <NumberInput
    key="max-distance"
    source="config.carRental.maxDistance"
    label="Distance maximale (km)"
    helperText="0 = illimité"
  />,
  <NumberInput
    key="max-locations"
    source="config.carRental.maxLocations"
    label="Nombre max de stations"
    helperText="0 = illimité"
  />,
  <SelectInput
    key="sort-type"
    source="config.carRental.searchSortType"
    label="Tri par défaut"
    choices={carRentalSortTypes}
    allowEmpty
  />,
];

export const DangerZoneInputs = () => [
  <div
    key="danger-warning"
    style={{
      background: "#fff3f3",
      border: "2px solid #ff4444",
      padding: "16px",
      borderRadius: "8px",
      fontFamily: "sans-serif",
      fontSize: "14px",
      lineHeight: "20px",
      margin: "0 16px 16px 0",
      width: "95%",
      color: "#cc0000",
    }}
  >
    Attention : ces paramètres affectent le fonctionnement critique de
    l&apos;entreprise.
  </div>,
  <BooleanInput key="test-mode" source="config.test" label="Mode test" />,
  <BooleanInput
    key="read-only"
    source="config.readOnly"
    label="Lecture seule"
  />,
];

// ---------------------------------------------------------------------------
// CustomerConfigInputs — Services externes tab (shared between Company & Entity)
// Removed: HiddenFlightPrice (moved to Voyages & Recherche tab)
// Removed: enableTravelersAnalytics (moved to Analytiques tab)
// Added: showFees, duffel maxOverrunPrice, jenji, carbookr config
// ---------------------------------------------------------------------------

export const CustomerConfigInputs = (title) => (
  <FormTab label={title}>
    <Typography variant="h5" style={styles.title}>
      Services activés
    </Typography>

    <SelectInput
      source="config.providers.rail"
      label="Fournisseur rail"
      allowEmpty
      format={(s) => (s != null && s.length > 0 ? s[0] : null)}
      parse={(v) => (v != null ? [v] : null)}
      alwaysOn
      choices={railProviders}
    />
    <SelectInput
      source="config.providers.air"
      label="Fournisseur aérien"
      allowEmpty
      format={(s) => (s != null && s.length > 0 ? s[0] : null)}
      parse={(v) => (v != null ? [v] : null)}
      alwaysOn
      choices={airProviders}
    />
    <SelectInput
      source="config.providers.travelerProfile"
      label="Profils voyageurs"
      allowEmpty
      format={(s) => (s != null && s.length > 0 ? s[0] : null)}
      parse={(v) => (v != null ? [v] : null)}
      alwaysOn
      choices={airProviders}
    />
    <SelectInput
      source="config.providers.hotel"
      label="Fournisseur hôtels"
      allowEmpty
      format={(s) => (s != null && s.length > 0 ? s[0] : null)}
      parse={(v) => (v != null ? [v] : null)}
      alwaysOn
      choices={hotelProviders}
    />
    <SelectInput
      source="config.providers.carRental"
      label="Fournisseur location voitures"
      allowEmpty
      format={(s) => (s != null && s.length > 0 ? s[0] : null)}
      parse={(v) => (v != null ? [v] : null)}
      alwaysOn
      choices={carRentalProviders}
    />

    <Typography variant="h5" style={styles.title}>
      CDS
    </Typography>
    <TextInput source="config.cdsProfile.dutyCode" label="Duty Code CDS" />

    <Typography variant="h5" style={styles.title}>
      Carbookr
    </Typography>
    <TextInput
      source="config.carbookrProfile.clientID"
      label="ClientID Carbookr"
    />

    <Typography variant="h5" style={styles.title}>
      Frais
    </Typography>
    <BooleanInput source="config.showFees" label="Afficher les frais" />

    <FormDataConsumer>
      {({ formData }) =>
        formData.config &&
        formData.config.providers &&
        formData.config.providers.air &&
        formData.config.providers.air.includes("duffel") && (
          <>
            <Typography variant="h5" style={styles.title}>
              Duffel
            </Typography>
            <NumberInput
              source="config.duffelCustomerProfile.maxOverrunPrice"
              label="Prix max dépassement Duffel"
              helperText="Montant maximal accepté de variation de prix entre la recherche et la réservation (en centimes)."
            />
          </>
        )
      }
    </FormDataConsumer>

    {process.env.REACT_APP_WITH_MARKUP === "true" && (
      <>
        <Typography variant="h5" style={styles.title}>
          Frais additionnels
        </Typography>

        <div
          style={{
            background: "#ffefd5",
            padding: "16px",
            borderRadius: "8px",
            fontFamily: "sans-serif",
            fontSize: "14px",
            lineHeight: "20px",
            margin: "0 16px 16px 0",
            width: "95%",
            color: "black",
          }}
        >
          Le Distributeur (agence de voyage) dispose techniquement de la
          possibilité de fixer librement les prix qu&apos;il communique à ses
          Clients et aux Utilisateurs des Services. A ce titre, il est rappelé
          que le rôle de the Treep se limite à celui d&apos;intermédiaire
          proposant des prix qui peuvent subir des variations avant d&apos;être
          présentés aux Clients et Utilisateurs, en fonction notamment des frais
          et commissions que le Distributeur applique librement et sur lequel
          the Treep n&apos;a aucune emprise. D&apos;une manière générale, the
          Treep ne saurait être, de quelque manière et sur quelque fondement que
          ce soit, tenu pour responsable de difficultés ou de litiges relatifs
          aux prix fixés par le Distributeur aux Clients et/ou Utilisateurs. Le
          Distributeur s&apos;engage par conséquent à garantir sans limitation
          de plafond the Treep des conséquences financières de tout recours
          judiciaire intenté par un tiers relatif aux prix affichés aux Clients
          et/ou Utilisateurs.
        </div>

        {markupFeesProviders.map((provider) => (
          <div
            style={{
              width: "100%",
              marginBottom: "24px",
              borderBottom: "2px solid #ccc",
            }}
            key={provider}
          >
            <Typography
              variant="subtitle1"
              style={{ textTransform: "capitalize" }}
            >
              Frais{" "}
              <span style={{ textTransform: "capitalize" }}>{provider}</span>
            </Typography>
            <div style={{ display: "flex", gap: "48px" }}>
              <NumberInput
                label="montant en centimes (€)"
                placeholder="Ex: 250 pour 2.5€"
                source={`config.markupFees.${provider}.amount`}
              />
              <NumberInput
                source={`config.markupFees.${provider}.percent`}
                step={0.1}
                label="pourcentage"
              />
            </div>
            <ArrayInput
              label="Codes tarifaires sur lesquels seront appliqués les frais supplémentaires. Si aucun code n'est renseigné, les frais s'appliqueront à tous les tarifs de ce fournisseur."
              source={`config.markupFees.${provider}.restrictedForFareCodes`}
            >
              <SimpleFormIterator disableReordering>
                <TextInput label="code tarifaire" />
              </SimpleFormIterator>
            </ArrayInput>
          </div>
        ))}
      </>
    )}
  </FormTab>
);

export const SabreCustomerConfigInputs = (title, ...rest) => (
  <FormTab label={title} {...rest}>
    <Typography variant="h5" style={styles.title}>
      Configurer Sabre
    </Typography>

    <Typography variant="h5" style={styles.title}>
      Profil Sabre à charger sur les PNR
    </Typography>
    <TextInput source="config.sabreProfile.pcc" label="PCC Sabre" />
    <TextInput source="config.sabreProfile.name" label="Nom profil Sabre" />
    <Typography variant="h5" style={styles.title}>
      Profil Sabre à lier aux profils créés
    </Typography>
    <TextInput
      source="config.sabrePaxCustomerProfile.number"
      label="Numéro profil voyageur"
    />
    <TextInput
      source="config.sabrePaxCustomerProfile.pcc"
      label="PCC profil voyageur"
    />
    <SelectInput
      source="config.sabrePaxCustomerProfile.type"
      label="Type de profil"
      alwaysOn
      allowEmpty
      choices={sabreProfileType}
    />

    <Typography variant="h5" style={styles.title}>
      Surcharger la configuration Sabre
    </Typography>
    <SabreConfigInput sourcePrefix="config.sabreConfigs" />
  </FormTab>
);

export const AmadeusCustomerConfigInputs = () => (
  <>
    <Typography variant="h5" style={styles.title}>
      Configurer Amadeus
    </Typography>

    <Typography variant="h5" style={styles.title}>
      Profil Amadeus à lier aux profils créés
    </Typography>
    <TextInput
      source="config.amadeusCustomerProfile.officeID"
      label="Office ID Amadeus"
    />
    <TextInput
      source="config.amadeusCustomerProfile.uniqueID"
      label="Unique ID Amadeus"
    />

    <Typography variant="h5" style={styles.title}>
      Surcharger la configuration Amadeus
    </Typography>
    {AmadeusConfigsInputs({ sourcePrefix: "config.amadeusConfigs." })}
  </>
);

// ---------------------------------------------------------------------------
// CompanyCreate
// ---------------------------------------------------------------------------

export const CompanyCreate = ({ permissions, ...props }) => (
  <SidebarFormLayout>
    <Create {...props}>
      <TabbedForm
        redirect="list"
        toolbar={<CustomToolbar />}
        tabs={<TabbedFormTabs orientation="vertical" />}
      >
        {/* Tab 1: Informations générales */}
        <FormTab label="Informations générales">
          <TextInput source="name" label="Nom" validate={[required()]} />
          <ReferenceInput
            source="config.travelAgencyID"
            label="Agence de voyage"
            reference="agencies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            validate={[required()]}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <SelectArrayInput
            source="payment_method"
            label="Méthode de paiement"
            defaultValue="ON_ACCOUNT"
            format={(s) => (s != null && s.trim() !== "" ? s.split(",") : null)}
            parse={(v) => (v != null && v.length > 0 ? v.join(",") : null)}
            choices={[
              { id: "ON_ACCOUNT", name: "Paiement entreprise" },
              { id: "CREDIT_CARD", name: "Carte de crédit" },
            ]}
          />
          <TextInput source="logo" label="Logo (URL)" />
          <TextInput source="fce_provider" label="Fournisseur FCE" />
          <BooleanInput
            source="disable_sending_thetreep_emails"
            label="Désactiver les emails TheTreep"
          />
          <BooleanInput
            source="enable_user_auto_enrollment"
            label="Inscription automatique utilisateurs"
            helperText="Permet aux utilisateurs dont le domaine d'email correspond à un domaine de l'entreprise d'être automatiquement acceptés lors de leur inscription, sans validation manuelle."
          />
          <TextInput
            source="domain"
            label="Domaines"
            helperText="Noms de domaine séparés par des virgules (ex : entreprise.com, filiale.fr). Utilisés pour l'inscription automatique."
          />
          <TextInput source="siret" label="SIRET" />
          <PlaceInput source="place" label="Adresse" />
          <TextInput source="contact" label="Contact" />
        </FormTab>

        {/* Tab 2: Configuration SNCF */}
        <FormTab label="Configuration SNCF">
          <TextInput source="fce" label="FCE SNCF" />
        </FormTab>

        {/* Tab 3: Voyages & Recherche (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Voyages & Recherche">
            <Typography variant="h5" style={styles.title}>
              Politique de voyage
            </Typography>
            <RichTextInput
              source="travel_policy"
              label="Politique de voyage"
              toolbar={[
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "align",
                "link",
                "image",
              ]}
            />
            <Typography variant="h5" style={styles.title}>
              Services activés
            </Typography>
            <BooleanInput source="hotel_enabled" label="Hôtels activés" />
            <BooleanInput
              source="carrental_enabled"
              label="Location de voitures activée"
            />

            {TravelSearchConfigInputs()}
          </FormTab>
        )}

        {/* Tab 3: Approbation & Sécurité (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Approbation & Sécurité">
            {ApprovalSecurityInputs()}
          </FormTab>
        )}

        {/* Tab 4: Analytiques (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Analytiques">{AnalyticsConfigInputs()}</FormTab>
        )}

        {/* Tab 5: Services externes (admin) */}
        {permissions != null &&
          permissions.includes("admin") &&
          CustomerConfigInputs("Services externes")}

        {/* Tab 6: Configuration Amadeus */}
        <FormTab label="Configuration Amadeus">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("amadeus")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("amadeus"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Amadeus
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Amadeus à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.amadeusCustomerProfile.officeID"
                      label="Office ID Amadeus"
                    />
                    <TextInput
                      source="config.amadeusCustomerProfile.uniqueID"
                      label="Unique ID Amadeus"
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Amadeus
                    </Typography>
                    {AmadeusConfigsInputs({
                      sourcePrefix: "config.amadeusConfigs.",
                    })}
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Amadeus dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 7: Configurer Sabre */}
        <FormTab label="Configurer Sabre">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("sabre")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("sabre"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Sabre
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à charger sur les PNR
                    </Typography>
                    <TextInput
                      source="config.sabreProfile.pcc"
                      label="PCC Sabre"
                    />
                    <TextInput
                      source="config.sabreProfile.name"
                      label="Nom profil Sabre"
                    />
                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.sabrePaxCustomerProfile.number"
                      label="Numéro profil voyageur"
                    />
                    <TextInput
                      source="config.sabrePaxCustomerProfile.pcc"
                      label="PCC profil voyageur"
                    />
                    <SelectInput
                      source="config.sabrePaxCustomerProfile.type"
                      label="Type de profil"
                      alwaysOn
                      allowEmpty
                      choices={sabreProfileType}
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Sabre
                    </Typography>
                    <SabreConfigInput sourcePrefix="config.sabreConfigs" />
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Sabre dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 8: Location de voitures */}
        <FormTab label="Location de voitures">
          <FormDataConsumer>
            {({ formData }) =>
              formData.carrental_enabled ? (
                <>{CarRentalConfigInputs()}</>
              ) : (
                <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez la location de voitures dans « Voyages &amp; Recherche
                  » pour configurer cet onglet.
                </Typography>
              )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 9: Ordres de mission (dev) */}
        {permissions != null && permissions.includes("dev") && (
          <FormTab label="Ordres de mission">
            <BooleanInput
              source="config.missionOrderConfig.enabled"
              label="Ordres de mission activés"
            />
            <FormDataConsumer>
              {({ formData }) =>
                formData.config &&
                formData.config.missionOrderConfig &&
                formData.config.missionOrderConfig.enabled && (
                  <MissionOrderConfigInputs />
                )
              }
            </FormDataConsumer>
          </FormTab>
        )}

        {/* Tab 10: Facturation (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Facturation">
            <Typography variant="h5" style={styles.title}>
              Facturation MCTO
            </Typography>
            <TextInput
              source="config.billingConf.customerName"
              label="Identifiant client MCTO"
              helperText="Ex: FC0059379"
            />
            <BooleanInput
              source="config.billingConf.enabled"
              label="Activer les MCTO"
            />
          </FormTab>
        )}

        {/* Tab 11: Templates Emailing (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Templates Emailing">
            <TextInput multiline source="email_header" label="En-tête" />
            <TextInput multiline source="email_footer" label="Pied de page" />
            <TextInput multiline source="email_onboarding" label="Onboarding" />
            <TextInput
              multiline
              source="email_confirmation"
              label="Confirmation"
            />
            <TextInput multiline source="email_welcome" label="Bienvenue" />
            <TextInput
              multiline
              source="email_password_reset"
              label="Réinitialisation mot de passe"
            />
            <TextInput
              multiline
              source="email_booking_confirmation"
              label="Confirmation de réservation"
            />
            <TextInput multiline source="email_approval" label="Approbation" />
            <TextInput
              multiline
              source="email_approval_pending"
              label="Approbation en attente"
            />
            <TextInput
              multiline
              source="email_approval_reminder"
              label="Rappel d'approbation"
            />
            <TextInput
              multiline
              source="email_approval_confirmation"
              label="Confirmation d'approbation"
            />
            <TextInput multiline source="email_invoice" label="Facture" />
          </FormTab>
        )}
      </TabbedForm>
    </Create>
  </SidebarFormLayout>
);

const CustomToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

// ---------------------------------------------------------------------------
// CompanyEdit — 13 tabs
// ---------------------------------------------------------------------------

export const CompanyEdit = ({ permissions, ...props }) => (
  <SidebarFormLayout>
    <Edit {...props}>
      <TabbedForm
        redirect="list"
        toolbar={<CustomToolbar />}
        tabs={<TabbedFormTabs orientation="vertical" />}
      >
        {/* Tab 1: Informations générales (simplified) */}
        <FormTab label="Informations générales">
          <TextInput disabled label="Id" source="uid" />
          <TextInput source="name" label="Nom" validate={[required()]} />
          <ReferenceInput
            source="config.travelAgencyID"
            label="Agence de voyage"
            reference="agencies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            validate={[required()]}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <SelectArrayInput
            source="payment_method"
            label="Méthode de paiement"
            defaultValue="ON_ACCOUNT"
            format={(s) => (s != null && s.trim() !== "" ? s.split(",") : null)}
            parse={(v) => (v != null && v.length > 0 ? v.join(",") : null)}
            choices={[
              { id: "ON_ACCOUNT", name: "Paiement entreprise" },
              { id: "CREDIT_CARD", name: "Carte de crédit" },
            ]}
          />
          <TextInput source="logo" label="Logo (URL)" />
          <TextInput source="fce_provider" label="Fournisseur FCE" />
          <BooleanInput
            source="disable_sending_thetreep_emails"
            label="Désactiver les emails TheTreep"
          />
          <BooleanInput
            source="enable_user_auto_enrollment"
            label="Inscription automatique utilisateurs"
            helperText="Permet aux utilisateurs dont le domaine d'email correspond à un domaine de l'entreprise d'être automatiquement acceptés lors de leur inscription, sans validation manuelle."
          />
          <TextInput
            source="domain"
            label="Domaines"
            helperText="Noms de domaine séparés par des virgules (ex : entreprise.com, filiale.fr). Utilisés pour l'inscription automatique."
          />
          <TextInput source="siret" label="SIRET" />
          <PlaceInput source="place" label="Adresse" />
          <TextInput source="contact" label="Contact" />
        </FormTab>

        {/* Tab 2: Configuration SNCF */}
        <FormTab label="Configuration SNCF">
          <TextInput source="fce" label="FCE SNCF" />
        </FormTab>

        {/* Tab 3: Voyages & Recherche (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Voyages & Recherche">
            <Typography variant="h5" style={styles.title}>
              Politique de voyage
            </Typography>
            <RichTextInput
              source="travel_policy"
              label="Politique de voyage"
              toolbar={[
                "bold",
                "italic",
                "underline",
                "strike",
                "blockquote",
                "align",
                "link",
                "image",
              ]}
            />
            <Typography variant="h5" style={styles.title}>
              Services activés
            </Typography>
            <BooleanInput source="hotel_enabled" label="Hôtels activés" />
            <BooleanInput
              source="carrental_enabled"
              label="Location de voitures activée"
            />

            {TravelSearchConfigInputs()}
          </FormTab>
        )}

        {/* Tab 3: Approbation & Sécurité (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Approbation & Sécurité">
            {ApprovalSecurityInputs()}
          </FormTab>
        )}

        {/* Tab 4: Analytiques (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Analytiques">{AnalyticsConfigInputs()}</FormTab>
        )}

        {/* Tab 5: Services externes (admin) */}
        {permissions != null &&
          permissions.includes("admin") &&
          CustomerConfigInputs("Services externes")}

        {/* Tab 6: Configuration Amadeus */}
        <FormTab label="Configuration Amadeus">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("amadeus")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("amadeus"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Amadeus
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Amadeus à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.amadeusCustomerProfile.officeID"
                      label="Office ID Amadeus"
                    />
                    <TextInput
                      source="config.amadeusCustomerProfile.uniqueID"
                      label="Unique ID Amadeus"
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Amadeus
                    </Typography>
                    {AmadeusConfigsInputs({
                      sourcePrefix: "config.amadeusConfigs.",
                    })}
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Amadeus dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 7: Configurer Sabre */}
        <FormTab label="Configurer Sabre">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("sabre")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("sabre"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Sabre
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à charger sur les PNR
                    </Typography>
                    <TextInput
                      source="config.sabreProfile.pcc"
                      label="PCC Sabre"
                    />
                    <TextInput
                      source="config.sabreProfile.name"
                      label="Nom profil Sabre"
                    />
                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.sabrePaxCustomerProfile.number"
                      label="Numéro profil voyageur"
                    />
                    <TextInput
                      source="config.sabrePaxCustomerProfile.pcc"
                      label="PCC profil voyageur"
                    />
                    <SelectInput
                      source="config.sabrePaxCustomerProfile.type"
                      label="Type de profil"
                      alwaysOn
                      allowEmpty
                      choices={sabreProfileType}
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Sabre
                    </Typography>
                    <SabreConfigInput sourcePrefix="config.sabreConfigs" />
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Sabre dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 8: Location de voitures */}
        <FormTab label="Location de voitures">
          <FormDataConsumer>
            {({ formData }) =>
              formData.carrental_enabled ? (
                <>{CarRentalConfigInputs()}</>
              ) : (
                <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez la location de voitures dans « Voyages &amp; Recherche
                  » pour configurer cet onglet.
                </Typography>
              )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 9: Ordres de mission (dev) */}
        {permissions != null && permissions.includes("dev") && (
          <FormTab label="Ordres de mission">
            <BooleanInput
              source="config.missionOrderConfig.enabled"
              label="Ordres de mission activés"
            />
            <FormDataConsumer>
              {({ formData }) =>
                formData.config &&
                formData.config.missionOrderConfig &&
                formData.config.missionOrderConfig.enabled && (
                  <MissionOrderConfigInputs />
                )
              }
            </FormDataConsumer>
          </FormTab>
        )}

        {/* Tab 10: Facturation (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Facturation">
            <Typography variant="h5" style={styles.title}>
              Facturation MCTO
            </Typography>
            <TextInput
              source="config.billingConf.customerName"
              label="Identifiant client MCTO"
              helperText="Ex: FC0059379"
            />
            <BooleanInput
              source="config.billingConf.enabled"
              label="Activer les MCTO"
            />
          </FormTab>
        )}

        {/* Tab 11: Templates Emailing (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Templates Emailing">
            <TextInput multiline source="email_header" label="En-tête" />
            <TextInput multiline source="email_footer" label="Pied de page" />
            <TextInput multiline source="email_onboarding" label="Onboarding" />
            <TextInput
              multiline
              source="email_confirmation"
              label="Confirmation"
            />
            <TextInput multiline source="email_welcome" label="Bienvenue" />
            <TextInput
              multiline
              source="email_password_reset"
              label="Réinitialisation mot de passe"
            />
            <TextInput
              multiline
              source="email_booking_confirmation"
              label="Confirmation de réservation"
            />
            <TextInput multiline source="email_approval" label="Approbation" />
            <TextInput
              multiline
              source="email_approval_pending"
              label="Approbation en attente"
            />
            <TextInput
              multiline
              source="email_approval_reminder"
              label="Rappel d'approbation"
            />
            <TextInput
              multiline
              source="email_approval_confirmation"
              label="Confirmation d'approbation"
            />
            <TextInput multiline source="email_invoice" label="Facture" />
          </FormTab>
        )}

        {/* Tab 12: Messages d'agence (admin) */}
        {permissions?.includes("admin") && (
          <FormTab label="Messages d'agence">
            <Typography variant="h5" style={{ ...styles.title, width: "100%" }}>
              Configuration des messages d&apos;agence
            </Typography>

            {categoryMessages.map((category) => (
              <div key={category.id} style={{ marginBottom: "16px" }}>
                <ArrayInput
                  source={`config.communicationsBySupport.${category.id}`}
                  label={category.name}
                  key={category.id}
                >
                  <SimpleFormIterator disableReordering>
                    <TextInput source="title" label="Titre" />
                    <TextInput source="content" label="Contenu" multiline />
                    <TextInput source="media" label="Lien média" />
                  </SimpleFormIterator>
                </ArrayInput>
              </div>
            ))}
          </FormTab>
        )}

        {/* Tab 13: Zone de danger (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Zone de danger">{DangerZoneInputs()}</FormTab>
        )}
      </TabbedForm>
    </Edit>
  </SidebarFormLayout>
);

// ---------------------------------------------------------------------------
// Show components (unchanged)
// ---------------------------------------------------------------------------

export const CustomerConfigFields = (title) => (
  <Tab label={title}>
    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Services activés
      </Typography>
    </NoBasePath>
    <ArrayField source="config.providers.rail" label="Fournisseur rail">
      <SingleFieldList>
        <ChipField />
      </SingleFieldList>
    </ArrayField>
    <ArrayField source="config.providers.air" label="Fournisseur aérien">
      <SingleFieldList>
        <ChipField />
      </SingleFieldList>
    </ArrayField>
    <ArrayField
      source="config.providers.travelerProfile"
      label="Profils voyageurs"
    >
      <SingleFieldList>
        <ChipField />
      </SingleFieldList>
    </ArrayField>
    <ArrayField source="config.providers.hotel" label="Fournisseur hôtels">
      <SingleFieldList>
        <ChipField />
      </SingleFieldList>
    </ArrayField>
    <ArrayField
      source="config.providers.carRental"
      label="Fournisseur location voitures"
    >
      <SingleFieldList>
        <ChipField />
      </SingleFieldList>
    </ArrayField>
    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Profil Sabre à charger sur les PNR
      </Typography>
    </NoBasePath>
    <TextField source="config.sabreProfile.pcc" label="PCC Sabre" />
    <TextField source="config.sabreProfile.name" label="Nom profil Sabre" />
    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Profil Sabre à lier aux profils créés
      </Typography>
    </NoBasePath>
    <TextField
      source="config.sabrePaxCustomerProfile.number"
      label="Numéro profil voyageur"
    />
    <TextField
      source="config.sabrePaxCustomerProfile.pcc"
      label="PCC profil voyageur"
    />
    <TextField
      source="config.sabrePaxCustomerProfile.type"
      label="Type de profil"
    />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Profil Amadeus à lier aux profils créés
      </Typography>
    </NoBasePath>
    <TextField
      source="config.amadeusCustomerProfile.officeID"
      label="Office ID Amadeus"
    />
    <TextField
      source="config.amadeusCustomerProfile.uniqueID"
      label="Unique ID Amadeus"
    />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        CDS
      </Typography>
    </NoBasePath>
    <TextField source="config.cdsProfile.dutyCode" label="Duty Code CDS" />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Carbookr
      </Typography>
    </NoBasePath>
    <TextField
      source="config.carbookrProfile.clientID"
      label="ClientID Carbookr"
    />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Surcharger la configuration Sabre
      </Typography>
    </NoBasePath>
    {SabreConfigsFields({ sourcePrefix: "config.sabreConfigs." })}

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Surcharger la configuration Amadeus
      </Typography>
    </NoBasePath>
    {SabreConfigsFields({ sourcePrefix: "config.amadeusConfigs." })}

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Surcharger la configuration sur les annulations hors lignes
      </Typography>
    </NoBasePath>
    <BooleanField
      source="config.railOfflineCancellation"
      label="Annulation rail offline"
    />
    <BooleanField
      source="config.airOfflineCancellation"
      label="Annulation aérien offline"
    />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Prix des billets
      </Typography>
    </NoBasePath>
    <BooleanField
      source="config.HiddenFlightPrice"
      label="Masquer les tarifs avions"
    />

    <NoBasePath>
      <Typography variant="h5" style={styles.title}>
        Champs analytiques voyageurs
      </Typography>
    </NoBasePath>
    <BooleanField
      source="config.enableTravelersAnalytics"
      label="Analytiques voyageurs éditables"
    />
  </Tab>
);

export const CompanyShow = ({ permissions, ...props }) => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Informations générales">
        <TextField source="id" label="Id" sortable={false} />
        <TextField source="name" label="Nom" sortable={false} />
        <TextField source="siret" label="SIRET" sortable={false} />
        <TextField source="domain" label="Domaine" sortable={false} />
        <ReferenceField
          source="config.travelAgencyID"
          label="Agence de voyage"
          reference="agencies"
          link="show"
          allowEmpty
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField
          source="place.formatted_address"
          label="Adresse"
          sortable={false}
        />
        <TextField
          source="payment_methods"
          label="Méthodes de paiement"
          sortable={false}
        />
        <TextField
          source="service_fees"
          label="Frais de service"
          sortable={false}
        />
        <TextField
          source="booking_pcc"
          label="PCC de réservation"
          sortable={false}
        />
        <TextField source="fce" label="FCE SNCF" sortable={false} />
        <RichTextField source="travel_policy" label="Politique de voyage" />
        <BooleanField source="config.test" label="Mode test" />
        <BooleanField
          source="config.oneClickApprovalEnabled"
          label="Approbation en un clic"
        />
        <BooleanField source="hotel_enabled" label="Hôtels activés" />
        <BooleanField
          source="carrental_enabled"
          label="Location de voitures activée"
        />
        <TextField source="config.approvalType" label="Type d'approbation" />
        <FunctionField
          label="Configuration de l'ordre de mission"
          render={({ config }) =>
            config &&
            config.missionOrderConfig &&
            Object.values(config.missionOrderConfig.enabled) ? (
                <MissionOrderConfigFields />
              ) : null
          }
        />
        <NoBasePath>
          <Typography variant="h5" style={styles.title}>
            Valeurs analytiques de l&apos;entreprise
          </Typography>
        </NoBasePath>
        <ArrayField source="config.analyticValues" label="Valeurs analytiques">
          <Datagrid>
            <TextField source="analytic.key" label="Clé SABRE/Amadeus" />
            <TextField source="analytic.cds_key" label="Clé CDS" />
            <TextField source="value" label="Valeur" />
          </Datagrid>
        </ArrayField>
      </Tab>
      {permissions != null &&
        permissions.includes("admin") &&
        CustomerConfigFields("Services externes")}
      {permissions != null && permissions.includes("admin") && (
        <Tab label="Templates Emailing">
          <RichTextField source="email_header" label="En-tête" />
          <RichTextField source="email_footer" label="Pied de page" />
          <RichTextField source="email_onboarding" label="Onboarding" />
          <RichTextField source="email_confirmation" label="Confirmation" />
          <RichTextField source="email_welcome" label="Bienvenue" />
          <RichTextField
            source="email_password_reset"
            label="Réinitialisation mot de passe"
          />
          <RichTextField
            source="email_booking_confirmation"
            label="Confirmation de réservation"
          />
          <RichTextField source="email_approval" label="Approbation" />
          <RichTextField
            source="email_approval_pending"
            label="Approbation en attente"
          />
          <RichTextField
            source="email_approval_reminder"
            label="Rappel d'approbation"
          />
          <RichTextField
            source="email_approval_confirmation"
            label="Confirmation d'approbation"
          />
        </Tab>
      )}
    </TabbedShowLayout>
  </Show>
);

// ---------------------------------------------------------------------------
// MissionOrderConfigInputs — FIXED: added contact field
// ---------------------------------------------------------------------------

export const MissionOrderConfigInputs = () => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Typography variant="h5" style={styles.title}>
        Configuration de l&apos;ordre de mission
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Nom de l'entreprise"
        source="config.missionOrderConfig.companyName"
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Préfixe du numéro"
        source="config.missionOrderConfig.prefix"
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Format de date"
        source="config.missionOrderConfig.dateFormat"
      />
    </Grid>
    <Grid item xs={12}>
      <NumberInput
        label="Nombre de chiffre du numéro d'increment"
        source="config.missionOrderConfig.digitNumber"
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput label="Logo" source="config.missionOrderConfig.logo" />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Intitulé de la signature"
        source="config.missionOrderConfig.signatureLabel"
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Lieu de la signature"
        source="config.missionOrderConfig.signatureLocation"
      />
    </Grid>
    <Grid item xs={12}>
      <TextInput label="Contact" source="config.missionOrderConfig.contact" />
    </Grid>
    <Grid item xs={12}>
      <TextInput
        label="Texte de pied de page"
        source="config.missionOrderConfig.footer"
      />
    </Grid>
  </Grid>
);

export const MissionOrderConfigFields = (title) => (
  <Grid container spacing={3}>
    <Grid item xs={12}>
      <Typography variant="h5" style={styles.title}>
        Configuration de l&apos;ordre de mission
      </Typography>
    </Grid>
    <Grid item xs={12}>
      <BooleanField
        label="Ordre de mission activé"
        source="config.missionOrderConfig.enabled"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Nom de l'entreprise"
        source="config.missionOrderConfig.companyName"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Préfixe du numéro"
        source="config.missionOrderConfig.prefix"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Format de date"
        source="config.missionOrderConfig.dateFormat"
      />
    </Grid>
    <Grid item xs={12}>
      <NumberField
        label="Nombre de chiffre du numéro d'increment"
        source="config.missionOrderConfig.digitNumber"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField label="Logo" source="config.missionOrderConfig.logo" />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Intitulé de la signature"
        source="config.missionOrderConfig.signatureLabel"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Lieu de la signature"
        source="config.missionOrderConfig.signatureLocation"
      />
    </Grid>
    <Grid item xs={12}>
      <TextField label="Contact" source="config.missionOrderConfig.contact" />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Texte de pied de page"
        source="config.missionOrderConfig.footer"
      />
    </Grid>
  </Grid>
);
